import { createStore } from '@/utils/createStore'
import { ProposalStatus, type PricingDetail, type ContractTerms, type BillingTerms, type ApprovalStep, type ProposalChangeLogEntry } from '@/types'

/**
 * Slim, list-facing proposal record. Dates are STRINGS (YYYY-MM-DD) so they
 * round-trip cleanly through JSON / localStorage without manual parsing.
 *
 * Fields mirror exactly what ProposalList.tsx renders in its columns.
 */
export interface ProposalRow {
  id: string
  referenceNumber?: string
  leadId: string
  /** Company / lead display name (denormalised from the lead at save time). */
  leadName?: string
  revisionNumber: number
  tariff: number // TNEB grid tariff
  sakthiTariff?: number // Sakthi Power commercial tariff
  commercialTerms: string
  status: ProposalStatus
  validity: number
  /** YYYY-MM-DD */
  createdAt: string
  /** YYYY-MM-DD */
  expiryDate: string
  template?: string
  /** Id of the template chosen on this proposal (so the PDF uses the right one). */
  templateId?: string
  deliveredTariff?: number
  /** Documents the customer must provide (pre-filled from the template, editable per proposal). */
  requiredDocuments?: string[]
  /** Documents to be signed (pre-filled from the template). */
  signingDocuments?: string[]

  // Template Overrides/Customizations
  bodyHtml?: string
  companyContentHtml?: string
  coverPageEnabled?: boolean
  coverTitle?: string
  coverSubtitle?: string
  coverDescription?: string
  coverImage?: string
  termsConditionsHtml?: string

  // ── Full proposal payload (so the Detail view / PDF reflect what was entered) ──
  /** Offer date — YYYY-MM-DD */
  offerDate?: string
  pricingDetails?: PricingDetail[]
  powerFacilitationCharge?: number
  gstOnFacilitationCharge?: number
  contractTerms?: ContractTerms
  billingTerms?: BillingTerms
  energySupplyAdjustments?: string
  gstDetails?: { hsn: string; sac: string; placeOfSupply: string; reverseCharge: boolean }
  additionalTerms?: string
  monthlyConsumption?: number
  /** Approval steps, built from the Role master's workflow matrix; tracks per-step status. */
  approvalChain?: ApprovalStep[]
  /** Negotiation history — populated when an Approved proposal is edited via "Negotiate". */
  changeLog?: ProposalChangeLogEntry[]
}

// Helpers to build dynamic YYYY-MM-DD strings relative to today (keeps the
// "expires in N days" UI meaningful regardless of when the demo is opened).
const iso = (d: Date) => d.toISOString().slice(0, 10)
const dateAgo = (days: number) => {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return iso(d)
}
const dateIn = (days: number) => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return iso(d)
}

export const proposalStore = createStore<ProposalRow>('sp_proposals', [], 'P', 2, '/proposals')
